import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import MeetingSummary from '../MeetingSummary';
import AppointmentSummary from '../AppointmentSummary';
import {
    updateDoctorNotes,
    updateDoctorNotes1,
    updatePreparationNotes,
    updateWrittenNotes,
    updateSummaryNotes,
    uploadAudio,
    uploadImage,
    deleteUploadedImage,
    deleteAudio,
} from '../../redux/appointmentSlice';
import { getFileURL } from '../../utils/fileURL';
import { getSocket } from '../../socket';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import MasterLabel from '../common/MasterLabel';
import SpecialtyIcon from '../common/SpecialtyIcon';
import { formatPrice } from '../../utils/currency';
import ClinicalHiddenNote from '../common/ClinicalHiddenNote';
import { canManageVisitMedia } from '../../utils/visitAccess';
import VisitReadingsPanel from '../checks/VisitReadingsPanel';
import ProgrammeCard from '../programmes/ProgrammeCard';
import { AppointmentProgrammeTag } from '../programmes/ProgrammeStopLine';
import usePlanControl from '../../hooks/usePlanControl';
import useVisitAiGates from '../../hooks/useVisitAiGates';
import { audioClipState, clipAwaitsTranscript, stampUploadAnswer } from '../../utils/visitTranscription';
import { serverReason } from '../patientTabs/prescriptionHelpers';
import { formatClockRange, formatTime } from '../../utils/clock';

const formatDateTime = (localTime) => {
    if (!localTime) return { date: '', time: '' };
    const dateObj = new Date(localTime);
    const date = dateObj.toLocaleDateString('en-CA');
    const time = formatTime(dateObj);
    return { date, time };
};

const MAX_NOTES_LENGTH = 2000;
const EMPTY_NOTES = {
    communication_therapist_patient: '',
    notes_of_doctor: '',
    preparation: '',
    consultation_notes: '',
    summary: '',
};

const AppointmentDetailsModal = ({ isOpen, onClose, appointment, onAppointmentUpdated }) => {
    const { t, i18n } = useTranslation(['appointmentdetails']);
    // 1 Oct 2026 (owner): an image is read by the AI, and the server refuses
    // the upload (403 PLAN_FEATURE_MISSING) when the DOCTOR's plan lacks
    // ai_ocr. A refusal's `message` is { en, fr } — handed to a toast as an
    // object it crashed the render, so every upload and delete refusal now
    // goes through serverReason.
    const ocrPlanOn = usePlanControl('ai_ocr');
    // 2 Oct 2026 (owner): the AUDIO upload is offered whatever the plan says —
    // without ai_transcription the clip is stored and playable, never
    // transcribed. These three decide only what is drawn while a transcript or
    // summary is still to come (off: no spinner, no poll).
    const aiGates = useVisitAiGates();
    const reason = (payload, fallback) => serverReason(payload, fallback, i18n?.language || 'en');
    const dispatch = useDispatch();
    const userRole = localStorage.getItem('Role');

    const [isEditing, setIsEditing] = useState(false);
    const [notes, setNotes] = useState(EMPTY_NOTES);
    const [notesErrors, setNotesErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [savingNoteSection, setSavingNoteSection] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [localAppointment, setLocalAppointment] = useState(appointment);
    const originalNotesRef = useRef(EMPTY_NOTES);

    // Audio / Image edit state
    const [audioFile, setAudioFile] = useState(null);
    const [audioPreview, setAudioPreview] = useState(null);
    const [audioError, setAudioError] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageError, setImageError] = useState('');

    const toggleExpandSection = (key) => {
        setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const clearAudioDraft = () => {
        setAudioFile(null);
        setAudioPreview(null);
        setAudioError('');
    };

    const clearImageDraft = () => {
        setImageFile(null);
        setImagePreview(null);
        setImageError('');
    };

    const handleAudioChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('audio/')) {
            setAudioError(t('appointmentdetails.invalid_audio_file', 'Please select a valid audio file'));
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            setAudioError(t('appointmentdetails.audio_too_large', 'Audio file must be under 20 MB'));
            return;
        }
        setAudioError('');
        setAudioFile(file);
        setAudioPreview(URL.createObjectURL(file));
    };

    const handleSaveAudio = async () => {
        if (!audioFile) return;
        setIsSaving(true);
        try {
            const result = await dispatch(uploadAudio({ appointmentId: appointment.appointmentId, audio: audioFile }));
            if (result?.meta?.requestStatus === 'fulfilled') {
                toast.success(t('appointmentdetails.audio_saved', 'Audio uploaded successfully'));
                const payload = result.payload || {};
                // The answer's `transcription` is a STATUS word ('queued' |
                // 'failed' | 'off' — owner, 2 Oct 2026), never the transcript:
                // 'off' is stamped on the clip it names, nothing is printed.
                setLocalAppointment((prev) => ({
                    ...prev,
                    audioUploads: stampUploadAnswer(payload.audioUploads || prev?.audioUploads || [], payload),
                    voiceClipUrl: payload.filePath || prev?.voiceClipUrl || '',
                }));
                clearAudioDraft();
                if (onAppointmentUpdated) onAppointmentUpdated();
            } else {
                toast.warn(reason(result?.payload, t('appointmentdetails.audio_save_error', 'Failed to upload audio')));
            }
        } catch (err) {
            toast.warn(t('appointmentdetails.audio_save_error', 'Failed to upload audio'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteAudioUpload = async (uploadId) => {
        if (!uploadId) return;
        const confirmResult = await Swal.fire({
            title: t('appointmentdetails.delete_audio_title', 'Delete audio?'),
            text: t('appointmentdetails.delete_audio_text', 'This will permanently remove the audio recording.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('appointmentdetails.delete_button', 'Delete'),
            cancelButtonText: t('appointmentdetails.cancel_button', 'Cancel'),
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
        });
        if (!confirmResult.isConfirmed) return;
        setIsSaving(true);
        try {
            const result = await dispatch(deleteAudio({ appointmentId: appointment.appointmentId, uploadId }));
            if (result?.meta?.requestStatus === 'fulfilled') {
                toast.success(t('appointmentdetails.audio_deleted', 'Audio deleted successfully'));
                const payload = result.payload || {};
                setLocalAppointment((prev) => {
                    const nextUploads = payload.audioUploads
                        || (prev?.audioUploads || []).filter((u) => u.uploadId !== uploadId);
                    const last = nextUploads.length > 0 ? nextUploads[nextUploads.length - 1] : null;
                    return {
                        ...prev,
                        audioUploads: nextUploads,
                        voiceClipUrl: last ? last.audioUrl : '',
                        transcribedNotes: last ? last.transcribedNotes : '',
                    };
                });
                if (onAppointmentUpdated) onAppointmentUpdated();
            } else {
                toast.warn(reason(result?.payload, t('appointmentdetails.audio_delete_error', 'Failed to delete audio')));
            }
        } catch (err) {
            toast.warn(t('appointmentdetails.audio_delete_error', 'Failed to delete audio'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setImageError(t('appointmentdetails.invalid_image_file', 'Please select a valid image file'));
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setImageError(t('appointmentdetails.image_too_large', 'Image must be under 5 MB'));
            return;
        }
        setImageError('');
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleSaveImage = async () => {
        if (!imageFile) return;
        setIsSaving(true);
        try {
            const result = await dispatch(uploadImage({ appointmentId: appointment.appointmentId, image: imageFile }));
            if (result?.meta?.requestStatus === 'fulfilled') {
                toast.success(t('appointmentdetails.image_saved', 'Image uploaded successfully'));
                const payload = result.payload || {};
                setLocalAppointment((prev) => ({
                    ...prev,
                    imageUploads: payload.imageUploads || prev?.imageUploads || [],
                    imageUrl: payload.imageUrl || prev?.imageUrl || '',
                    ocrNotes: payload.extractedText !== undefined ? payload.extractedText : prev?.ocrNotes,
                }));
                clearImageDraft();
                if (onAppointmentUpdated) onAppointmentUpdated();
            } else {
                toast.warn(reason(result?.payload, t('appointmentdetails.image_save_error', 'Failed to upload image')));
            }
        } catch (err) {
            toast.warn(t('appointmentdetails.image_save_error', 'Failed to upload image'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteImage = async (uploadId) => {
        if (!uploadId) return;
        const confirmResult = await Swal.fire({
            title: t('appointmentdetails.delete_image_title', 'Delete image?'),
            text: t('appointmentdetails.delete_image_text', 'This will permanently remove the image.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('appointmentdetails.delete_button', 'Delete'),
            cancelButtonText: t('appointmentdetails.cancel_button', 'Cancel'),
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
        });
        if (!confirmResult.isConfirmed) return;
        setIsSaving(true);
        try {
            const result = await dispatch(deleteUploadedImage({ appointmentId: appointment.appointmentId, uploadId }));
            if (result?.meta?.requestStatus === 'fulfilled') {
                toast.success(t('appointmentdetails.image_deleted', 'Image deleted successfully'));
                const payload = result.payload || {};
                setLocalAppointment((prev) => {
                    const nextUploads = payload.imageUploads
                        || (prev?.imageUploads || []).filter((u) => u.uploadId !== uploadId);
                    const last = nextUploads.length > 0 ? nextUploads[nextUploads.length - 1] : null;
                    return {
                        ...prev,
                        imageUploads: nextUploads,
                        imageUrl: last ? last.imageUrl : '',
                        ocrNotes: last ? last.ocrNotes : '',
                    };
                });
                if (onAppointmentUpdated) onAppointmentUpdated();
            } else {
                toast.warn(reason(result?.payload, t('appointmentdetails.image_delete_error', 'Failed to delete image')));
            }
        } catch (err) {
            toast.warn(t('appointmentdetails.image_delete_error', 'Failed to delete image'));
        } finally {
            setIsSaving(false);
        }
    };

    // Sync local appointment + notes state from appointment prop
    useEffect(() => {
        if (!appointment) return;
        setLocalAppointment(appointment);
        const initial = {
            communication_therapist_patient: appointment.description || '',
            notes_of_doctor: appointment.doctorDescription || '',
            preparation: appointment.preparationNotes || '',
            consultation_notes: appointment.writtenNotes || '',
            summary: appointment.summaryNotes || '',
        };
        setNotes(initial);
        originalNotesRef.current = initial;
    }, [appointment]);

    // Live async results: the backend pushes transcript:ready / ocr:ready over
    // Socket.IO when a worker finishes. Patch the matching upload item so the
    // "Generating…" placeholder is replaced by the real text without a refresh.
    // The socket may not be connected yet when this mounts, so retry attaching
    // (mirrors MeetingSummary/AppointmentSummary's pattern).
    useEffect(() => {
        let alive = true;
        let retryTimer = null;
        let attachedSocket = null;
        const onTranscript = ({ appointmentId, uploadId, transcribedNotes, status }) => {
            setLocalAppointment((prev) => {
                if (!prev || (appointmentId && prev.appointmentId !== appointmentId)) return prev;
                return {
                    ...prev,
                    audioUploads: (prev.audioUploads || []).map((u) =>
                        u.uploadId === uploadId ? { ...u, transcribedNotes: transcribedNotes || '', status: status || 'done' } : u),
                };
            });
        };
        const onOcr = ({ appointmentId, uploadId, ocrNotes, status }) => {
            setLocalAppointment((prev) => {
                if (!prev || (appointmentId && prev.appointmentId !== appointmentId)) return prev;
                return {
                    ...prev,
                    imageUploads: (prev.imageUploads || []).map((u) =>
                        u.uploadId === uploadId ? { ...u, ocrNotes: ocrNotes || '', status: status || 'done' } : u),
                };
            });
        };
        const attach = () => {
            if (!alive) return;
            const socket = getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attachedSocket = socket;
            socket.on('transcript:ready', onTranscript);
            socket.on('ocr:ready', onOcr);
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attachedSocket) {
                attachedSocket.off('transcript:ready', onTranscript);
                attachedSocket.off('ocr:ready', onOcr);
            }
        };
    }, []);

    // Fallback for lost socket events (tab backgrounded, reconnect mid-emit,
    // etc.): while any audio/image upload on the open appointment is still
    // 'processing', poll its status directly and patch localAppointment the
    // same way the socket handler does.
    const axiosInstance = useAxiosAuth();
    useEffect(() => {
        const appointmentId = localAppointment?.appointmentId;
        // A clip stored without a transcript is waiting on nothing.
        const hasProcessing = (localAppointment?.audioUploads || []).some((u) => clipAwaitsTranscript(u, aiGates.audioTranscription))
            || (localAppointment?.imageUploads || []).some((u) => u.status === 'processing');
        if (!appointmentId || !hasProcessing) return undefined;

        let alive = true;
        const timer = setTimeout(async () => {
            if (!alive) return;
            try {
                const res = await axiosInstance.get(`${baseURL}/appointment/${appointmentId}`);
                const fresh = res.data?.data || res.data;
                setLocalAppointment((prev) => {
                    if (!prev || prev.appointmentId !== appointmentId) return prev;
                    return {
                        ...prev,
                        audioUploads: (prev.audioUploads || []).map((u) => {
                            if (u.status !== 'processing') return u;
                            const match = (fresh?.audioUploads || []).find((f) => f.uploadId === u.uploadId);
                            return match && match.status !== 'processing' ? { ...u, ...match } : u;
                        }),
                        imageUploads: (prev.imageUploads || []).map((u) => {
                            if (u.status !== 'processing') return u;
                            const match = (fresh?.imageUploads || []).find((f) => f.uploadId === u.uploadId);
                            return match && match.status !== 'processing' ? { ...u, ...match } : u;
                        }),
                    };
                });
            } catch {
                // silent — next tick (or the socket) will catch up
            }
        }, 6000);
        return () => { alive = false; clearTimeout(timer); };
    }, [localAppointment?.appointmentId, localAppointment?.audioUploads, localAppointment?.imageUploads, aiGates.audioTranscription]);

    // Reset edit mode whenever modal closes or appointment changes
    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
            setNotesErrors({});
            setExpandedSections({});
            clearAudioDraft();
            clearImageDraft();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, appointment?.appointmentId]);

    if (!isOpen || !appointment) return null;

    const { date, time } = formatDateTime(appointment.localTime);
    // The row the list / calendar handed us was shaped by the server for THIS
    // login (appointmentsForViewer): a caller the clinical-record rule refuses
    // gets the notes, transcripts, OCR, AI summary and media emptied and
    // `clinicalHidden: true`. Those empty fields are not "nothing recorded",
    // so none of them is drawn — one note says why instead.
    const clinicalHidden = appointment.clinicalHidden === true;
    // Editing writes notes and uploads / deletes media, which the server allows
    // only the visit's OWN doctor (a colleague is refused), so a doctor edits
    // only a visit that is theirs.
    const canEdit = userRole === 'doctor'
        && appointment.status === 'Completed'
        && !clinicalHidden
        && canManageVisitMedia(appointment, userRole, localStorage.getItem('ID'));

    const validateNotesInput = (value) => {
        if (value && value.length > MAX_NOTES_LENGTH) {
            return t('appointmentdetails.notes_max_length', `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`);
        }
        return '';
    };

    const hasUnsavedChanges = () => {
        const original = originalNotesRef.current;
        return Object.keys(notes).some((key) => notes[key] !== (original[key] || ''));
    };

    const exitEditMode = () => {
        setNotes({ ...originalNotesRef.current });
        setNotesErrors({});
        setIsEditing(false);
    };

    const handleCancelEdit = async () => {
        if (!hasUnsavedChanges()) {
            setIsEditing(false);
            return;
        }
        const result = await Swal.fire({
            title: t('appointmentdetails.unsaved_changes_title', 'Unsaved Changes'),
            text: t('appointmentdetails.unsaved_changes_text', 'You have unsaved changes. What would you like to do?'),
            icon: 'warning',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('appointmentdetails.save_and_continue', 'Save & Continue'),
            denyButtonText: t('appointmentdetails.discard_changes', 'Discard'),
            cancelButtonText: t('appointmentdetails.stay', 'Stay'),
            confirmButtonColor: '#4ABDBD',
            denyButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            allowOutsideClick: false,
        });

        if (result.isConfirmed) {
            const original = originalNotesRef.current;
            for (const section of Object.keys(notes)) {
                if (notes[section] !== (original[section] || '') && notes[section].trim()) {
                    await handleSaveNotes(section, { silent: true });
                }
            }
            setIsEditing(false);
        } else if (result.isDenied) {
            exitEditMode();
        }
    };

    const handleSaveNotes = async (section, options = {}) => {
        const noteContent = notes[section];
        const error = validateNotesInput(noteContent);
        if (error) {
            setNotesErrors((prev) => ({ ...prev, [section]: error }));
            return;
        }
        if (!noteContent || !noteContent.trim()) {
            if (!options.silent) {
                toast.warn(t('appointmentdetails.empty_notes_error', 'Notes cannot be empty'));
            }
            return;
        }
        setNotesErrors((prev) => ({ ...prev, [section]: '' }));
        setIsSaving(true);
        setSavingNoteSection(section);

        const appointmentId = appointment.appointmentId;
        const doctorId = appointment.doctorId;

        try {
            let result;
            switch (section) {
                case 'communication_therapist_patient':
                    result = await dispatch(updateDoctorNotes({ appointmentId, doctorId, description: noteContent }));
                    break;
                case 'notes_of_doctor':
                    result = await dispatch(updateDoctorNotes1({ appointmentId, doctorId, notes: noteContent }));
                    break;
                case 'preparation':
                    result = await dispatch(updatePreparationNotes({ appointmentId, notes: noteContent }));
                    break;
                case 'consultation_notes':
                    result = await dispatch(updateWrittenNotes({ appointmentId, notes: noteContent }));
                    break;
                case 'summary':
                    result = await dispatch(updateSummaryNotes({ appointmentId, notes: noteContent }));
                    break;
                default:
                    return;
            }

            if (result?.meta?.requestStatus === 'fulfilled') {
                if (!options.silent) {
                    toast.success(t('appointmentdetails.save_success_message', 'Notes updated successfully'));
                }
                originalNotesRef.current = { ...originalNotesRef.current, [section]: noteContent };
                if (onAppointmentUpdated) onAppointmentUpdated();
            } else {
                toast.warn(reason(result?.payload, t('appointmentdetails.save_error_message', 'Failed to update notes')));
            }
        } catch (err) {
            toast.warn(t('appointmentdetails.save_error_message', 'Failed to update notes'));
        } finally {
            setIsSaving(false);
            setSavingNoteSection(null);
        }
    };

    const noteSections = [
        {
            key: 'communication_therapist_patient',
            title: t('appointmentdetails.communication_therapist_patient'),
            sourceValue: appointment.description,
        },
        {
            key: 'notes_of_doctor',
            title: t('appointmentdetails.notes_of_doctor'),
            sourceValue: appointment.doctorDescription,
        },
        {
            key: 'preparation',
            title: t('appointmentdetails.preparation'),
            sourceValue: appointment.preparationNotes,
        },
        {
            key: 'consultation_notes',
            title: t('appointmentdetails.consultation_notes'),
            sourceValue: appointment.writtenNotes,
        },
        {
            key: 'summary',
            title: t('appointmentdetails.summary'),
            sourceValue: appointment.summaryNotes,
        },
    ];

    // Render an async transcript/OCR result, or a "Generating…" spinner while the
    // worker is still processing it.
    const mediaTextBlock = (text, status, generatingLabel) => {
        // The worker reports a miss the same way it reports success, with
        // status 'failed' and no text (services/mediaResultConsumer.js). Saying
        // nothing here is what left the doctor on a spinner that had already
        // stopped: they could not tell "still working" from "never arrived".
        if (status === 'failed') {
            return (
                <p className="mt-2 w-full p-3 border border-amber-200 rounded-xl bg-amber-50 text-sm text-amber-700">
                    {t('appointmentdetails.media_failed', 'This did not come back from the transcription service. Upload it again to retry.')}
                </p>
            );
        }
        if (status === 'processing') {
            return (
                <p className="mt-2 w-full p-3 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-400 italic flex items-center gap-2">
                    <span className="h-3 w-3 border-2 border-gray-300 border-t-[#4ABDBD] rounded-full animate-spin inline-block" />
                    {generatingLabel}
                </p>
            );
        }
        if (!text) return null;
        return (
            <p className="mt-2 w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 whitespace-pre-wrap">
                {text}
            </p>
        );
    };

    // One prescription audio clip's transcript line: where no transcript will
    // come, "the audio is saved" — never "Generating…" (owner, 2 Oct 2026).
    const audioTextBlock = (upload, idx) => {
        if (audioClipState(upload, aiGates.audioTranscription) === 'off') {
            return (
                <p data-testid={`appointment-details-modal-audio-no-transcription-${idx}`} className="mt-2 text-xs text-gray-500">
                    {t('planGate.audio_saved_no_transcription', 'Transcription is not included — the audio is saved.')}
                </p>
            );
        }
        return mediaTextBlock(upload.transcribedNotes, upload.status, t('appointmentdetails.generating_transcript', 'Generating transcript…'));
    };

    return (
        <div
            data-testid="appointment-details-modal"
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4"
        >
            <div data-testid="appointment-details-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                {/* Header Section - Sticky */}
                <div data-testid="appointment-details-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                    <h3 data-testid="appointment-details-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('appointmentdetails.heading')}
                    </h3>
                    <button
                        data-testid="appointment-details-modal-close-button"
                        onClick={onClose}
                        className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                    >
                        <X className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div data-testid="appointment-details-modal-body" className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                    {/* Title Section */}
                    <div data-testid="appointment-details-modal-title-section" className="text-center mb-4 sm:mb-6">
                        <p data-testid="appointment-details-modal-specialty-name" className="text-xl sm:text-2xl md:text-3xl font-bold inline-flex items-center justify-center gap-2" style={{ color: appointment.colour || '#4ABDBD' }}>
                            {/* Inherits the heading colour through currentColor, so it is tinted like the text. */}
                            <SpecialtyIcon specialtyId={appointment.SpecialtyId} size={28} className="w-[1em] h-[1em]" />
                            <MasterLabel type="masterSpecialty" id={appointment.SpecialtyId} field="name" fallback={appointment.specialtyName} />
                        </p>
                        <p data-testid="appointment-details-modal-specialty-type" className="text-sm sm:text-base font-semibold" style={{ color: appointment.colour || '#4ABDBD' }}>
                            {appointment.specialtyTypeName}
                        </p>
                        {/* A programme visit: "<programme> · Stop n" (and an
                            emergency's reason), from the appointment's own
                            `programme` (doc/programme-visits.md §3). */}
                        <AppointmentProgrammeTag programme={appointment.programme} className="mt-2" testId="appointment-details-modal-programme" />
                    </div>

                    {/* AI meeting summary + transcript — clinical, so not requested
                        at all for a row the server flagged clinicalHidden. */}
                    {clinicalHidden ? (
                        <ClinicalHiddenNote className="mb-4 sm:mb-6" testId="appointment-details-modal-clinical-hidden" />
                    ) : (
                        <>
                            <MeetingSummary appointmentId={appointment.appointmentId} expectRecording={/visio/i.test(appointment?.appointmentMode || '')} transcriptionOn={aiGates.recordingTranscription} className="mb-4 sm:mb-6" />
                            <AppointmentSummary appointment={appointment} generationOn={aiGates.summary} className="mb-4 sm:mb-6" />
                        </>
                    )}

                    {/* Info Badges */}
                    <div data-testid="appointment-details-modal-info-badges" className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
                        {[
                            { label: t('appointmentdetails.date_label', 'Date'), value: date },
                            { label: t('appointmentdetails.time_label', 'Time'), value: time },
                            {
                                label: t('appointmentdetails.status_label', 'Status'),
                                value: t(`appointmentdetails.${appointment.status}`) || appointment.status || 'Booked',
                                className: 'text-green-600'
                            },
                            {
                                label: t('appointmentdetails.price_label', 'Price'),
                                value: appointment.price ? formatPrice(appointment.price, appointment.currency) : '-',
                                priceType: appointment.pricetype === 'Type 1' ? t('appointmentdetails.price_type_appointment') :
                                    appointment.pricetype === 'Type 2' ? t('appointmentdetails.price_type_custom') :
                                        appointment.pricetype === 'Type 3' ? t('appointmentdetails.price_type_manual') : null
                            },
                            {
                                label: t('appointmentdetails.mode_label', 'Mode'),
                                value: appointment.appointmentMode === 'onsite' ? t(`appointmentdetails.Onsite`) : t('appointmentdetails.Visio')
                            }
                        ].map((item, index) => (
                            <div data-testid={`appointment-details-modal-info-badge-${index}`} key={index} className="flex items-center gap-2">
                                <p data-testid={`appointment-details-modal-info-label-${index}`} className="text-xs sm:text-sm font-semibold text-gray-500">{item.label}:</p>
                                <div className="flex flex-col">
                                    <p data-testid={`appointment-details-modal-info-value-${index}`} className={`text-xs sm:text-sm font-medium text-gray-400 ${item.className || ''}`}>
                                        {item.value}
                                    </p>
                                </div>
                                {item.priceType && (
                                    <p data-testid={`appointment-details-modal-info-price-type-${index}`} className="text-[10px] sm:text-xs font-medium text-green-600 mt-0.5">
                                        {item.priceType}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Check readings (check sets): chips per reading taken and
                        "Record readings" for the desk — the doctor and staff;
                        NOT the hospital admin (owner, 28 Sep 2026: the admin
                        manages the sets, never a visit's readings; the API
                        refuses its record / correct too). Draws nothing for a
                        viewer who may not record or a hospital without it. */}
                    {(userRole === 'doctor' || userRole === 'user') && (
                        <VisitReadingsPanel appointment={appointment} className="mb-4 sm:mb-6" />
                    )}
                    {/* The programme DESK STRIP: the patient's running
                        programmes as their stop line, read-only — staff with
                        checks.record; not the admin (same decision). Draws
                        nothing otherwise. */}
                    {userRole === 'user' && appointment?.patientId && (
                        <ProgrammeCard patientId={appointment.patientId} appointment={appointment} variant="desk" className="mb-4 sm:mb-6" />
                    )}

                    {/* Details (notes, audio+transcript, image+OCR, prescription) show
                        regardless of appointment status — the status itself is already
                        shown in the info badges above. Empty sections self-hide. */}
                    {(
                        <>
                            {/* Five editable note sections */}
                            {noteSections.map((section) => {
                                const hasValue = !!section.sourceValue;
                                if (!isEditing && !hasValue) return null;

                                return (
                                    <div data-testid={`appointment-details-modal-note-section-${section.key}`} key={section.key} className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                        <h2 data-testid={`appointment-details-modal-note-title-${section.key}`} className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                            {section.title}
                                        </h2>
                                        {isEditing ? (
                                            <>
                                                <textarea
                                                    data-testid={`appointment-details-modal-note-textarea-${section.key}`}
                                                    placeholder={t('appointmentdetails.textarea_placeholder', 'Enter notes...')}
                                                    value={notes[section.key]}
                                                    onChange={(e) => {
                                                        const sanitized = e.target.value.replace(/[<>]/g, '');
                                                        const finalValue = sanitized.length > MAX_NOTES_LENGTH
                                                            ? sanitized.substring(0, MAX_NOTES_LENGTH)
                                                            : sanitized;
                                                        setNotes((prev) => ({ ...prev, [section.key]: finalValue }));
                                                        setNotesErrors((prev) => ({ ...prev, [section.key]: validateNotesInput(finalValue) }));
                                                    }}
                                                    disabled={isSaving}
                                                    maxLength={MAX_NOTES_LENGTH}
                                                    className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] resize-none text-sm transition-all duration-300 ${expandedSections[section.key] ? 'min-h-[300px]' : 'min-h-[100px]'} ${notesErrors[section.key]
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                        }`}
                                                />
                                                {notesErrors[section.key] && (
                                                    <p data-testid={`appointment-details-modal-note-error-${section.key}`} className="text-red-600 text-xs mt-1">{notesErrors[section.key]}</p>
                                                )}
                                                <div className="flex justify-end gap-2 sm:gap-3">
                                                    <button
                                                        data-testid={`appointment-details-modal-note-expand-button-${section.key}`}
                                                        type="button"
                                                        onClick={() => toggleExpandSection(section.key)}
                                                        disabled={isSaving}
                                                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer disabled:opacity-50"
                                                    >
                                                        {expandedSections[section.key]
                                                            ? t('appointmentdetails.collapse_button', 'Collapse')
                                                            : t('appointmentdetails.expand_button', 'Expand')}
                                                    </button>
                                                    <button
                                                        data-testid={`appointment-details-modal-note-save-button-${section.key}`}
                                                        type="button"
                                                        onClick={() => handleSaveNotes(section.key)}
                                                        disabled={isSaving}
                                                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                    >
                                                        {savingNoteSection === section.key && (
                                                            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        )}
                                                        {t('appointmentdetails.save_button', 'Save')}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <p data-testid={`appointment-details-modal-note-text-${section.key}`} className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px] whitespace-pre-wrap">
                                                {notes[section.key] || t('appointmentdetails.no_data')}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Audio Section — multi-clip, mirrors the Image Section */}
                            {isEditing ? (
                                <div data-testid="appointment-details-modal-audio-edit-section" className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                    <div className="flex items-center justify-between">
                                        <h2 data-testid="appointment-details-modal-audio-heading" className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                            {t('appointmentdetails.upload_audio_heading')}
                                        </h2>
                                        <span data-testid="appointment-details-modal-audio-count" className="text-xs text-gray-500">
                                            {(localAppointment?.audioUploads || []).length}/5
                                        </span>
                                    </div>
                                    {(localAppointment?.audioUploads || []).length < 5 && (
                                        <div className="relative">
                                            <input
                                                data-testid="appointment-details-modal-audio-input"
                                                type="file"
                                                accept="audio/*"
                                                onChange={handleAudioChange}
                                                disabled={isSaving}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />
                                            <div data-testid="appointment-details-modal-audio-upload-area" className="border-2 border-dashed border-[#4ABDBD] rounded-xl p-6 text-center bg-gradient-to-br from-[#4ABDBD]/5 to-[#3AA8A8]/5 hover:from-[#4ABDBD]/10 hover:to-[#3AA8A8]/10 transition-all duration-300 hover:border-[#3AA8A8]">
                                                <p data-testid="appointment-details-modal-audio-upload-text" className="text-sm font-semibold text-[#4ABDBD]">
                                                    {t('appointmentdetails.click_to_upload_audio', 'Click to upload audio')}
                                                </p>
                                                <p data-testid="appointment-details-modal-audio-format-hint" className="text-xs text-gray-500 mt-1">
                                                    {t('appointmentdetails.audio_format_hint', 'MP3, WAV, M4A up to 20MB')}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {audioError && (
                                        <p data-testid="appointment-details-modal-audio-error" className="text-red-600 text-xs sm:text-sm mt-2">{audioError}</p>
                                    )}
                                    {audioPreview && (
                                        <div data-testid="appointment-details-modal-audio-preview-container" className="mt-3 bg-white rounded-xl border border-gray-200 p-3">
                                            <p data-testid="appointment-details-modal-audio-preview-label" className="text-xs sm:text-sm font-semibold text-gray-500 mb-2">
                                                {t('appointmentdetails.audio_preview_label', 'Audio preview')}
                                            </p>
                                            <audio
                                                data-testid="appointment-details-modal-audio-player"
                                                controls
                                                src={audioPreview}
                                                className="w-full"
                                            />
                                            <div className="flex justify-end gap-2 sm:gap-3 mt-3">
                                                <button
                                                    data-testid="appointment-details-modal-audio-clear-button"
                                                    type="button"
                                                    onClick={clearAudioDraft}
                                                    disabled={isSaving}
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer disabled:opacity-50"
                                                >
                                                    {t('appointmentdetails.clear_button', 'Clear')}
                                                </button>
                                                <button
                                                    data-testid="appointment-details-modal-audio-save-button"
                                                    type="button"
                                                    onClick={handleSaveAudio}
                                                    disabled={isSaving}
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                >
                                                    {isSaving && (
                                                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    )}
                                                    {t('appointmentdetails.save_button', 'Save')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {(localAppointment?.audioUploads || []).length > 0 && (
                                        <div data-testid="appointment-details-modal-audio-uploads-list" className="mt-3 flex flex-col gap-3">
                                            {localAppointment.audioUploads.map((upload, idx) => (
                                                <div data-testid={`appointment-details-modal-audio-upload-item-${idx}`} key={upload.uploadId || idx} className="relative bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span data-testid={`appointment-details-modal-audio-upload-index-${idx}`} className="text-xs text-gray-500 font-medium">
                                                            {t('appointmentdetails.audio_clip_label', 'Audio')} {idx + 1}
                                                        </span>
                                                        <button
                                                            data-testid={`appointment-details-modal-audio-delete-button-${idx}`}
                                                            type="button"
                                                            onClick={() => handleDeleteAudioUpload(upload.uploadId)}
                                                            disabled={isSaving || !upload.uploadId}
                                                            title={t('appointmentdetails.delete_button', 'Delete')}
                                                            className="bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-md cursor-pointer disabled:opacity-50 transition-all duration-200"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                    <audio
                                                        data-testid={`appointment-details-modal-audio-upload-player-${idx}`}
                                                        controls
                                                        src={getFileURL(upload.audioUrl)}
                                                        className="w-full"
                                                    />
                                                    {audioTextBlock(upload, idx)}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : ((localAppointment?.audioUploads?.length > 0 || localAppointment?.voiceClipUrl) && (
                                <div data-testid="appointment-details-modal-audio-view-section" className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                    <h2 data-testid="appointment-details-modal-audio-view-heading" className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                        {t('appointmentdetails.upload_audio_heading')}
                                        {localAppointment?.audioUploads?.length > 1 && (
                                            <span data-testid="appointment-details-modal-audio-view-count" className="text-xs text-gray-400 ml-2 font-normal">({localAppointment.audioUploads.length})</span>
                                        )}
                                    </h2>
                                    {localAppointment?.audioUploads?.length > 0 ? (
                                        <div data-testid="appointment-details-modal-audio-view-list" className="flex flex-col gap-3">
                                            {localAppointment.audioUploads.map((upload, idx) => (
                                                <div data-testid={`appointment-details-modal-audio-view-item-${idx}`} key={upload.uploadId || idx} className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                                                    <span className="text-xs text-gray-500 font-medium">{t('appointmentdetails.audio_clip_label', 'Audio')} {idx + 1}</span>
                                                    <audio
                                                        data-testid={`appointment-details-modal-audio-view-player-${idx}`}
                                                        controls
                                                        src={getFileURL(upload.audioUrl)}
                                                        className="w-full mt-2"
                                                    />
                                                    {(() => {
                                                        const clipState = audioClipState(upload, aiGates.audioTranscription);
                                                        if (clipState === 'off') return audioTextBlock(upload, idx);
                                                        if (clipState !== 'processing' && clipState !== 'text') return null;
                                                        return (
                                                            <div className="mt-2" data-testid={`appointment-details-modal-audio-view-transcript-${idx}`}>
                                                                <p className="text-xs sm:text-sm font-semibold text-gray-500 mb-1">{t('appointmentdetails.transcribed_notes_label')}:</p>
                                                                {mediaTextBlock(upload.transcribedNotes, upload.status, t('appointmentdetails.generating_transcript', 'Generating transcript…'))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            ))}
                                        </div>
                                    ) : localAppointment?.voiceClipUrl && (
                                        <>
                                            <audio data-testid="appointment-details-modal-audio-view-player" controls src={getFileURL(localAppointment.voiceClipUrl)} className="w-full max-w-md" />
                                            {localAppointment.transcribedNotes && (
                                                <div data-testid="appointment-details-modal-transcribed-notes-container" className="mt-3">
                                                    <p data-testid="appointment-details-modal-transcribed-notes-label" className="text-xs sm:text-sm font-semibold text-gray-500 mb-2">
                                                        {t('appointmentdetails.transcribed_notes_label')}:
                                                    </p>
                                                    <p data-testid="appointment-details-modal-transcribed-notes-text" className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px] whitespace-pre-wrap">
                                                        {localAppointment.transcribedNotes}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}

                            {/* Image Section */}
                            {isEditing ? (
                                <div data-testid="appointment-details-modal-image-edit-section" className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                    <div className="flex items-center justify-between">
                                        <h2 data-testid="appointment-details-modal-image-heading" className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                            {t('appointmentdetails.upload_image_heading', 'Upload Image')}
                                        </h2>
                                        <span data-testid="appointment-details-modal-image-count" className="text-xs text-gray-500">
                                            {(localAppointment?.imageUploads || []).length}/5
                                        </span>
                                    </div>
                                    {!ocrPlanOn && (
                                        <p data-testid="appointment-details-modal-image-not-in-plan" className="text-xs text-gray-500">
                                            {t('planGate.not_in_plan_ocr', 'Reading notes from an image is not included in your plan.')}
                                        </p>
                                    )}
                                    {ocrPlanOn && (localAppointment?.imageUploads || []).length < 5 && (
                                        <div className="relative">
                                            <input
                                                data-testid="appointment-details-modal-image-input"
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                disabled={isSaving}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />
                                            <div data-testid="appointment-details-modal-image-upload-area" className="border-2 border-dashed border-[#4ABDBD] rounded-xl p-6 text-center bg-gradient-to-br from-[#4ABDBD]/5 to-[#3AA8A8]/5 hover:from-[#4ABDBD]/10 hover:to-[#3AA8A8]/10 transition-all duration-300 hover:border-[#3AA8A8]">
                                                <p data-testid="appointment-details-modal-image-upload-text" className="text-sm font-semibold text-[#4ABDBD]">
                                                    {t('appointmentdetails.click_to_upload_image', 'Click to upload image')}
                                                </p>
                                                <p data-testid="appointment-details-modal-image-format-hint" className="text-xs text-gray-500 mt-1">
                                                    {t('appointmentdetails.image_format_hint', 'PNG, JPG up to 5MB')}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {imageError && (
                                        <p data-testid="appointment-details-modal-image-error" className="text-red-600 text-xs sm:text-sm mt-2">{imageError}</p>
                                    )}
                                    {imagePreview && (
                                        <div data-testid="appointment-details-modal-image-preview-container" className="mt-3 bg-white rounded-xl border border-gray-200 p-3">
                                            <img
                                                data-testid="appointment-details-modal-image-preview"
                                                src={imagePreview}
                                                alt={t('appointmentdetails.image_alt', 'Image preview')}
                                                className="w-full max-w-md h-auto rounded-lg border border-gray-200"
                                            />
                                            <div className="flex justify-end gap-2 mt-3">
                                                <button
                                                    data-testid="appointment-details-modal-image-clear-button"
                                                    type="button"
                                                    onClick={clearImageDraft}
                                                    disabled={isSaving}
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer disabled:opacity-50"
                                                >
                                                    {t('appointmentdetails.clear_button', 'Clear')}
                                                </button>
                                                <button
                                                    data-testid="appointment-details-modal-image-save-button"
                                                    type="button"
                                                    onClick={handleSaveImage}
                                                    disabled={isSaving}
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                >
                                                    {isSaving && (
                                                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    )}
                                                    {t('appointmentdetails.save_button', 'Save')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {(localAppointment?.imageUploads || []).length > 0 && (
                                        <div data-testid="appointment-details-modal-image-uploads-grid" className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                                            {localAppointment.imageUploads.map((upload, idx) => (
                                                <div data-testid={`appointment-details-modal-image-upload-item-${idx}`} key={upload.uploadId || idx} className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                                    <img
                                                        data-testid={`appointment-details-modal-image-upload-thumb-${idx}`}
                                                        src={getFileURL(upload.imageUrl)}
                                                        alt={`${t('appointmentdetails.uploaded_image_alt')} ${idx + 1}`}
                                                        className="w-full h-20 object-cover cursor-pointer"
                                                        onClick={() => window.open(getFileURL(upload.imageUrl), '_blank')}
                                                    />
                                                    <button
                                                        data-testid={`appointment-details-modal-image-delete-button-${idx}`}
                                                        type="button"
                                                        onClick={() => handleDeleteImage(upload.uploadId)}
                                                        disabled={isSaving || !upload.uploadId}
                                                        title={t('appointmentdetails.delete_button', 'Delete')}
                                                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-md cursor-pointer disabled:opacity-50 transition-all duration-200"
                                                    >
                                                        ×
                                                    </button>
                                                    <div className="px-2 py-1 text-center">
                                                        <span data-testid={`appointment-details-modal-image-upload-index-${idx}`} className="text-[10px] text-gray-500 font-medium">{idx + 1}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : ((localAppointment?.imageUploads?.length > 0 || localAppointment?.imageUrl) && (
                                <div data-testid="appointment-details-modal-image-view-section" className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                    <h2 data-testid="appointment-details-modal-image-view-heading" className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                        {t('appointmentdetails.uploaded_image_label')}
                                        {localAppointment?.imageUploads?.length > 1 && (
                                            <span data-testid="appointment-details-modal-image-view-count" className="text-xs text-gray-400 ml-2 font-normal">({localAppointment.imageUploads.length})</span>
                                        )}
                                    </h2>
                                    {localAppointment?.imageUploads?.length > 0 ? (
                                        <div data-testid="appointment-details-modal-image-view-list" className="flex flex-col gap-3">
                                            {localAppointment.imageUploads.map((upload, idx) => (
                                                <div data-testid={`appointment-details-modal-image-view-item-${idx}`} key={upload.uploadId || idx} className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                                                    <span className="text-xs text-gray-500 font-medium">{t('appointmentdetails.uploaded_image_alt')} {idx + 1}</span>
                                                    <img
                                                        data-testid={`appointment-details-modal-image-view-img-${idx}`}
                                                        src={getFileURL(upload.imageUrl)}
                                                        alt={`${t('appointmentdetails.uploaded_image_alt')} ${idx + 1}`}
                                                        className="w-full h-auto rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow mt-2"
                                                        onClick={() => window.open(getFileURL(upload.imageUrl), '_blank')}
                                                    />
                                                    {(upload.status === 'processing' || upload.ocrNotes) && (
                                                        <div className="mt-2" data-testid={`appointment-details-modal-ocr-notes-text-${idx}`}>
                                                            <p className="text-xs sm:text-sm font-semibold text-gray-500 mb-1">{t('appointmentdetails.ocr_notes_label')}:</p>
                                                            {mediaTextBlock(upload.ocrNotes, upload.status, t('appointmentdetails.generating_ocr', 'Generating OCR…'))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : localAppointment?.imageUrl && (
                                        <>
                                            <img
                                                data-testid="appointment-details-modal-image-view-single"
                                                src={getFileURL(localAppointment.imageUrl)}
                                                alt={t('appointmentdetails.uploaded_image_alt')}
                                                className="w-full max-w-md h-auto rounded border border-gray-300"
                                            />
                                            {localAppointment.ocrNotes && (
                                                <div data-testid="appointment-details-modal-ocr-notes-single-container" className="mt-3">
                                                    <p data-testid="appointment-details-modal-ocr-notes-single-label" className="text-xs sm:text-sm font-semibold text-gray-500 mb-2">
                                                        {t('appointmentdetails.ocr_notes_label')}:
                                                    </p>
                                                    <p data-testid="appointment-details-modal-ocr-notes-single-text" className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px] whitespace-pre-wrap">
                                                        {localAppointment.ocrNotes}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}

                            {!isEditing && (<>
                            {/* Patient Information */}
                            <div data-testid="appointment-details-modal-patient-info-section" className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                <h2 data-testid="appointment-details-modal-patient-info-heading" className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                    {t('appointmentdetails.patient_information')}
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 border border-gray-300 rounded-xl bg-gray-50">
                                    <div>
                                        <p data-testid="appointment-details-modal-patient-name-label" className="text-xs sm:text-sm font-semibold text-gray-500">
                                            {t('appointmentdetails.patient_name_label')}:
                                        </p>
                                        <p data-testid="appointment-details-modal-patient-name-value" className="text-xs sm:text-sm font-medium text-gray-600">
                                            {appointment.patientName ? `${appointment.patientName} ${appointment.patientSurname || ''}` : '-'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Appointment Information */}
                            <div data-testid="appointment-details-modal-appointment-info-section" className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                <h2 data-testid="appointment-details-modal-appointment-info-heading" className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                    {t('appointmentdetails.appointment_information')}
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 border border-gray-300 rounded-xl bg-gray-50">
                                    <div>
                                        <p data-testid="appointment-details-modal-duration-label" className="text-xs sm:text-sm font-semibold text-gray-500">
                                            {t('appointmentdetails.duration_label')}:
                                        </p>
                                        <p data-testid="appointment-details-modal-duration-value" className="text-xs sm:text-sm font-medium text-gray-600">
                                            {appointment.duration} {t('appointmentdetails.minutes')}
                                        </p>
                                    </div>
                                    <div>
                                        <p data-testid="appointment-details-modal-address-type-label" className="text-xs sm:text-sm font-semibold text-gray-500">
                                            {t('appointmentdetails.address_type_label')}:
                                        </p>
                                        <p data-testid="appointment-details-modal-address-type-value" className="text-xs sm:text-sm font-medium text-gray-600">
                                            {appointment.addressTypeName || '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <p data-testid="appointment-details-modal-time-slot-label" className="text-xs sm:text-sm font-semibold text-gray-500">
                                            {t('appointmentdetails.time_slot_label')}:
                                        </p>
                                        <p data-testid="appointment-details-modal-time-slot-value" className="text-xs sm:text-sm font-medium text-gray-600">
                                            {formatClockRange(appointment.timeSlot, appointment.slotEndTime)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Information */}
                            <div data-testid="appointment-details-modal-payment-info-section" className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                <h2 data-testid="appointment-details-modal-payment-info-heading" className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                    {t('appointmentdetails.payment_information')}
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 border border-gray-300 rounded-xl bg-gray-50">
                                    <div>
                                        <p data-testid="appointment-details-modal-payment-method-label" className="text-xs sm:text-sm font-semibold text-gray-500">
                                            {t('appointmentdetails.payment_method_label')}:
                                        </p>
                                        <p data-testid="appointment-details-modal-payment-method-value" className="text-xs sm:text-sm font-medium text-gray-600">
                                            {appointment.payment?.method ? t(`appointmentdetails.${appointment.payment.method}`, appointment.payment.method) : t('appointmentdetails.na', '-')}
                                        </p>
                                    </div>
                                    <div>
                                        <p data-testid="appointment-details-modal-payment-status-label" className="text-xs sm:text-sm font-semibold text-gray-500">
                                            {t('appointmentdetails.payment_status_label')}:
                                        </p>
                                        <p data-testid="appointment-details-modal-payment-status-value" className="text-xs sm:text-sm font-medium text-gray-600">
                                            {appointment.payment?.status ? t(`appointmentdetails.${appointment.payment.status}`, appointment.payment.status) : t('appointmentdetails.na', '-')}
                                        </p>
                                    </div>
                                    <div>
                                        <p data-testid="appointment-details-modal-payment-date-label" className="text-xs sm:text-sm font-semibold text-gray-500">
                                            {t('appointmentdetails.payment_date_label')}:
                                        </p>
                                        <p data-testid="appointment-details-modal-payment-date-value" className="text-xs sm:text-sm font-medium text-gray-600">
                                            {appointment.payment?.date ? new Date(appointment.payment.date).toLocaleDateString() : '-'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            </>)}
                        </>
                    )}
                </div>

                {/* Footer Section - Sticky */}
                <div data-testid="appointment-details-modal-footer" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-2 sm:gap-4">
                    {canEdit && !isEditing && (
                        <button
                            data-testid="appointment-details-modal-edit-button"
                            type="button"
                            onClick={() => setIsEditing(true)}
                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        >
                            {t('appointmentdetails.edit_button', 'Edit')}
                        </button>
                    )}
                    {canEdit && isEditing && (
                        <button
                            data-testid="appointment-details-modal-cancel-button"
                            type="button"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer disabled:opacity-50"
                        >
                            {t('appointmentdetails.cancel_button', 'Cancel')}
                        </button>
                    )}
                    {!isEditing && (
                        <button
                            data-testid="appointment-details-modal-footer-close-button"
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        >
                            {t('appointmentdetails.close_button', 'Close')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppointmentDetailsModal;
